import { lazy } from 'react';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const UsersList = lazy(() => import('./pages/users/UsersList'));
const UserDetail = lazy(() => import('./pages/users/UserDetail'));
const RolesList = lazy(() => import('./pages/roles/RolesList'));
const CandidatesList = lazy(() => import('./pages/candidates/CandidatesList'));
const CandidateRegister = lazy(() => import('./pages/candidates/CandidateRegister'));
const CandidateWorkspace = lazy(() => import('./pages/candidates/CandidateWorkspace'));
const PendingStepEdits = lazy(() => import('./pages/candidates/PendingStepEdits'));
const StepApprovalRequests = lazy(() => import('./pages/candidates/StepApprovalRequests'));
const StepSubmissionRequests = lazy(() => import('./pages/candidates/StepSubmissionRequests'));
const WorkflowNotifications = lazy(() => import('./pages/candidates/WorkflowNotifications'));
const CandidateDocuments = lazy(() => import('./pages/candidates/CandidateDocuments'));
const CandidateTimeline = lazy(() => import('./pages/candidates/CandidateTimeline'));
const CandidateRequestPage = lazy(() => import('./pages/candidates/CandidateRequestPage'));
const CandidateRequestApprovals = lazy(() => import('./pages/candidates/CandidateRequestApprovals'));
const PassportRegister = lazy(() => import('./pages/candidates/PassportRegister'));
const CollectedPassports = lazy(() => import('./pages/candidates/CollectedPassports'));
const AccommodationRegister = lazy(() => import('./pages/candidates/AccommodationRegister'));
const PassportApprovals = lazy(() => import('./pages/candidates/PassportApprovals'));
const PassportReasonMasters = lazy(() => import('./pages/masters/PassportReasonMasters'));
const RequestLocationMasters = lazy(() => import('./pages/masters/RequestLocationMasters'));
const BulkRequest = lazy(() => import('./pages/candidates/BulkRequest'));
const BulkRequestApprovals = lazy(() => import('./pages/candidates/BulkRequestApprovals'));
const MyRejectedRequests = lazy(() => import('./pages/candidates/MyRejectedRequests'));
const OtherCandidates = lazy(() => import('./pages/candidates/OtherCandidates'));
const AssignedSteps = lazy(() => import('./pages/assigned/AssignedSteps'));
const ScheduledSteps = lazy(() => import('./pages/assigned/ScheduledSteps'));
const LeadsList = lazy(() => import('./pages/leads/LeadsList'));
const LeadRegister = lazy(() => import('./pages/leads/LeadRegister'));
const LeadDetail = lazy(() => import('./pages/leads/LeadDetail'));
const RejectedApplicants = lazy(() => import('./pages/leads/RejectedApplicants'));
const LeadMasters = lazy(() => import('./pages/masters/LeadMasters'));
const NationalityMasters = lazy(() => import('./pages/masters/NationalityMasters'));
const RejectionReasonMasters = lazy(() => import('./pages/masters/RejectionReasonMasters'));
const BulkRequestTypeMasters = lazy(() => import('./pages/masters/BulkRequestTypeMasters'));
const WorkflowReference = lazy(() => import('./pages/workflow/WorkflowReference'));
const WorkflowDocuments = lazy(() => import('./pages/workflow/WorkflowDocuments'));
const ExcelTemplatesPage = lazy(() => import('./pages/workflow/ExcelTemplatesPage'));
const Reports = lazy(() => import('./pages/reports/Reports'));
const ActivityLog = lazy(() => import('./pages/activity/ActivityLog'));

// Every page is code-split: its chunk is only downloaded when the route is first visited, instead of
// shipping the whole app in the first bundle. App.js wraps the routed page in <Suspense>.

const LEADS_VIEW_PERM = ['leads.view-own', 'leads.view-all'];
const MASTERS_PERM = ['masters.view', 'masters.manage'];
const OTHER_CANDIDATES_PERM = ['candidates.revert-withdrawn', 'candidates.revert-suspension', 'candidates.revert-hold'];
const STEP_APPROVAL_PERM = ['workflow.approval.level1', 'workflow.approval.level2', 'workflow.approval.level3'];

// Central route table: every authenticated route names the permission that guards it
// (null = auth only, no specific permission; an array means any one of those permissions is
// enough). `nav` marks entries that appear in the sidebar — an optional `nav.group` nests the
// entry under a collapsible parent instead of showing it at the top level. App.js renders
// `routes`, Sidebar.jsx renders the `navItems` tree built from the `nav` subset.
export const routes = [
  { path: '/', perm: 'dashboard.view', element: <Dashboard />, nav: { label: 'Dashboard', order: 1 } },
  {
    path: '/candidates',
    perm: 'candidates.view',
    element: <CandidatesList />,
    nav: { label: 'All Candidates', order: 2, group: 'Candidates' },
  },
  {
    path: '/candidates/other',
    perm: OTHER_CANDIDATES_PERM,
    element: <OtherCandidates />,
    nav: { label: 'Other Candidates', order: 2.45, group: 'Candidates' },
  },
  {
    path: '/candidates/pending-edits',
    perm: 'approve edit candidate completed step',
    element: <PendingStepEdits />,
    nav: { label: 'Edit Approvals', order: 2.5, group: 'Approvals', badge: 'step-edits' },
  },
  {
    path: '/candidates/approval-requests',
    perm: STEP_APPROVAL_PERM,
    element: <StepApprovalRequests />,
    nav: { label: 'Level Approvals', order: 2.6, group: 'Approvals', badge: 'step-approvals' },
  },
  {
    path: '/candidates/submission-requests',
    perm: 'workflow.step-submission-approval',
    element: <StepSubmissionRequests />,
    nav: { label: 'Step Approval', order: 2.7, group: 'Approvals', badge: 'step-submissions' },
  },
  {
    path: '/candidates/workflow-notifications',
    perm: null,
    element: <WorkflowNotifications />,
    nav: { label: 'Workflow Notifications', order: 2.75, group: 'Candidates', badge: 'workflow-notifications' },
  },
  {
    path: '/candidates/documents',
    perm: 'documents.view',
    element: <CandidateDocuments />,
    nav: { label: 'Documents', order: 2.45, group: 'Candidates' },
  },
  {
    path: '/candidates/timeline',
    perm: 'candidates.view',
    element: <CandidateTimeline />,
    nav: { label: 'Timeline', order: 2.46, group: 'Candidates' },
  },
  {
    path: '/candidates/bulk-request',
    perm: 'candidates.bulk-requests',
    element: <BulkRequest />,
    nav: { label: 'Requests', order: 2.4, group: 'Requests' },
  },
  {
    path: '/candidates/rejected-requests',
    perm: ['candidates.bulk-requests', 'passport-requests.create', 'accommodation-requests.create'],
    element: <MyRejectedRequests />,
    nav: { label: 'Rejected Requests', order: 2.45, group: 'Requests', badge: 'rejected-requests' },
  },
  {
    path: '/candidates/passport-requests',
    perm: 'passport-requests.create',
    element: <PassportRegister />,
    nav: { label: 'Request', order: 2.41, group: 'Passport' },
  },
  {
    path: '/candidates/passport-register',
    perm: ['passport-requests.create', 'passport-requests.approve'],
    element: <CollectedPassports />,
    nav: { label: 'Register', order: 2.74, group: 'Passport' },
  },
  {
    path: '/candidates/passport-request-approvals',
    perm: 'passport-requests.approve',
    element: <PassportApprovals />,
    nav: { label: 'Approval', order: 2.75, group: 'Passport', badge: 'passport-requests' },
  },
  {
    path: '/candidates/accommodation-requests',
    perm: 'accommodation-requests.create',
    element: <CandidateRequestPage type="accommodation" />,
    nav: { label: 'Request', order: 2.42, group: 'Accommodation' },
  },
  {
    path: '/candidates/accommodation-register',
    perm: 'accommodation-requests.approve',
    element: <AccommodationRegister />,
    nav: { label: 'Register', order: 2.77, group: 'Accommodation' },
  },
  {
    path: '/candidates/accommodation-request-approvals',
    perm: 'accommodation-requests.approve',
    element: <CandidateRequestApprovals type="accommodation" />,
    nav: { label: 'Approval', order: 2.76, group: 'Accommodation', badge: 'accommodation-requests' },
  },
  {
    path: '/candidates/bulk-request-approvals',
    perm: 'candidates.bulk-request-approvals',
    element: <BulkRequestApprovals />,
    nav: { label: 'Requests Approval', order: 2.9, group: 'Requests', badge: 'bulk-requests' },
  },
  {
    path: '/assigned-jobs',
    perm: null,
    element: <AssignedSteps />,
    nav: { label: 'Assigned Step', order: 2.95, group: 'Assigned Jobs', badge: 'assigned-jobs' },
  },
  {
    path: '/assigned-jobs/scheduled',
    perm: 'workflow.step-submission-approval',
    element: <ScheduledSteps />,
    nav: { label: 'Scheduled Steps', order: 2.96, group: 'Assigned Jobs' },
  },
  { path: '/candidates/new', perm: 'candidates.create', element: <CandidateRegister /> },
  { path: '/candidates/:id', perm: 'candidates.view', element: <CandidateWorkspace /> },
  {
    path: '/leads',
    perm: LEADS_VIEW_PERM,
    element: <LeadsList />,
    nav: { label: 'Applicants', order: 1.5 },
  },
  {
    path: '/leads/new',
    perm: 'leads.create',
    element: <LeadRegister />,
    nav: { label: 'Register', order: 1.9, group: 'Candidates' },
  },
  {
    path: '/leads/rejected',
    perm: 'leads.view-rejected',
    element: <RejectedApplicants />,
    nav: { label: 'Rejected Applicants', order: 1.6 },
  },
  { path: '/leads/:id', perm: LEADS_VIEW_PERM, element: <LeadDetail /> },
  {
    path: '/users',
    perm: 'users.view',
    element: <UsersList />,
    nav: { label: 'Users', order: 4 },
  },
  { path: '/users/:id', perm: 'users.view', element: <UserDetail /> },
  {
    path: '/roles',
    perm: 'roles.view',
    element: <RolesList />,
    nav: { label: 'Roles & Permissions', order: 5 },
  },
  {
    path: '/workflow',
    perm: 'workflow.view',
    element: <WorkflowReference />,
    nav: { label: 'Steps', order: 6, group: 'Workflow' },
  },
  {
    path: '/workflow/documents',
    perm: ['workflow.view', 'workflow.documents.manage'],
    element: <WorkflowDocuments />,
    nav: { label: 'Documents', order: 6.1, group: 'Workflow' },
  },
  {
    path: '/workflow/excel-templates',
    perm: 'excel-templates.request',
    element: <ExcelTemplatesPage />,
    nav: { label: 'Excel Templates', order: 6.2, group: 'Workflow' },
  },
  {
    path: '/reports',
    perm: 'reports.view',
    element: <Reports />,
    nav: { label: 'Reports', order: 7 },
  },
  {
    path: '/activity-log',
    perm: 'activity-logs.view',
    element: <ActivityLog />,
    nav: { label: 'Activity Log', order: 8 },
  },
  {
    path: '/masters/leads',
    perm: MASTERS_PERM,
    element: <LeadMasters />,
    nav: { label: 'Source', order: 9, group: 'Masters' },
  },
  {
    path: '/masters/nationalities',
    perm: MASTERS_PERM,
    element: <NationalityMasters />,
    nav: { label: 'Nationalities', order: 10, group: 'Masters' },
  },
  {
    path: '/masters/rejection-reasons',
    perm: MASTERS_PERM,
    element: <RejectionReasonMasters />,
    nav: { label: 'Rejection Reasons', order: 11, group: 'Masters' },
  },
  {
    path: '/masters/bulk-request-types',
    perm: MASTERS_PERM,
    element: <BulkRequestTypeMasters />,
    nav: { label: 'Bulk Request', order: 12, group: 'Masters' },
  },
  {
    path: '/masters/passport-reasons',
    perm: MASTERS_PERM,
    element: <PassportReasonMasters />,
    nav: { label: 'Passport Reasons', order: 13, group: 'Masters' },
  },
  {
    path: '/masters/locations',
    perm: MASTERS_PERM,
    element: <RequestLocationMasters />,
    nav: { label: 'Locations', order: 14, group: 'Masters' },
  },
  { path: '/profile', perm: null, element: <Profile /> },
];

// Builds the sidebar tree: top-level entries, plus one level of grouped entries (e.g. everything
// under "Masters") ordered by the position of the first child that introduces the group.
function buildNavTree() {
  const navRoutes = routes.filter((r) => r.nav).sort((a, b) => a.nav.order - b.nav.order);
  const tree = [];
  const groupNodes = new Map();

  for (const route of navRoutes) {
    const entry = { path: route.path, perm: route.perm, label: route.nav.label, badge: route.nav.badge };

    if (route.nav.group) {
      let groupNode = groupNodes.get(route.nav.group);
      if (!groupNode) {
        groupNode = { label: route.nav.group, order: route.nav.order, children: [] };
        groupNodes.set(route.nav.group, groupNode);
        tree.push(groupNode);
      }
      groupNode.children.push(entry);
    } else {
      tree.push({ ...entry, order: route.nav.order });
    }
  }

  return tree.sort((a, b) => a.order - b.order);
}

export const navItems = buildNavTree();
